import { z } from 'zod';
import type { ReadinessItemDto } from './connections';
import { passwordSchema } from './users';

/**
 * 212Ez as a platform (owner's request, 2026-10-30): an MVNO signs itself up and gets its own
 * company with an administrator; it starts in Trial, where services it has not connected are
 * simulated, and the platform moves it to Live in the platform console.
 */

export const COMPANY_STAGES = ['Trial', 'Live'] as const;
export type CompanyStage = (typeof COMPANY_STAGES)[number];

export const signupSchema = z.object({
  companyName: z.string().trim().min(2, 'Company name is required').max(80),
  firstName: z.string().trim().min(1, 'First Name is required').max(20),
  lastName: z.string().trim().min(1, 'Last Name is required').max(20),
  email: z
    .string()
    .trim()
    .min(1, 'Email Address is required')
    .max(50)
    .email('Enter a valid email address'),
  phone: z
    .string()
    .trim()
    .regex(/^\d{10}$/, 'Phone Number must be 10 digits'),
  loginId: z
    .string()
    .trim()
    .min(3, 'Login Id must be at least 3 characters')
    .max(30)
    .regex(/^[A-Za-z0-9._-]+$/, 'Login ID may contain letters, digits, dot, dash and underscore'),
  password: passwordSchema,
  /** The carriers or MVNEs the MVNO works with, as it writes them (for the platform's follow-up). */
  carriers: z.string().trim().max(200).optional().default(''),
});
export type SignupInput = z.input<typeof signupSchema>;

/** A company a super admin makes on the platform console: a temporary password is given to it. */
export const companyCreateSchema = signupSchema.omit({ password: true });
export type CompanyCreateInput = z.input<typeof companyCreateSchema>;

export interface CompanyCreatedDto extends SignupResultDto {
  /** Shown once. It works one time, to choose a new password. */
  temporaryPassword: string;
}

/**
 * 212Ez's own staff, the levels above every company's chain (owner, 2026-11-05). Access flows upward:
 * each level also has everything the levels below it have.
 */
export const STAFF_ROLES = ['Super user', 'Engineer', 'Customer Associate', 'Sales'] as const;
export type StaffRole = (typeof STAFF_ROLES)[number];

const SALES_CAN = ['analytics', 'companies.create'] as const;
const ASSOCIATE_CAN = [...SALES_CAN, 'companies.open'] as const;
const ENGINEER_CAN = [
  ...ASSOCIATE_CAN,
  'credentials',
  'companies.manage',
  'logs',
  'connectors',
] as const;
const SUPER_CAN = [...ENGINEER_CAN, 'staff'] as const;
export type StaffCapability = (typeof SUPER_CAN)[number];

/** What each level may do; the one place to change it. */
export const STAFF_CAN: Record<StaffRole, readonly StaffCapability[]> = {
  'Super user': SUPER_CAN,
  Engineer: ENGINEER_CAN,
  'Customer Associate': ASSOCIATE_CAN,
  Sales: SALES_CAN,
};
export const staffCan = (role: StaffRole | undefined, what: StaffCapability): boolean =>
  STAFF_CAN[role ?? 'Super user'].includes(what);

export const superAdminCreateSchema = z.object({
  role: z.enum(STAFF_ROLES).default('Super user'),
  loginId: signupSchema.shape.loginId,
  name: z.string().trim().min(1, 'Name is required').max(60),
  email: signupSchema.shape.email,
  password: passwordSchema,
});
export const superAdminLoginSchema = z.object({
  loginId: z.string().trim().min(1, 'Login ID is required').max(30),
  password: z.string().min(1, 'Password is required').max(200),
});
export const superAdminStatusSchema = z.object({ status: z.enum(['Active', 'Inactive']) });

export interface SuperAdminDto {
  id: string;
  loginId: string;
  name: string;
  email: string;
  role: StaffRole;
  status: 'Active' | 'Inactive';
  lastLoginAt: string | null;
  createdAt: string;
}

export interface PlatformAuthStateDto {
  /** No super admin exists yet: the first is made with the release console password. */
  needsFirstSuperAdmin: boolean;
  signedIn: { loginId: string; name: string; role: StaffRole; can: StaffCapability[] } | null;
  /** The release switch `platform-new-company`: the New company form shows only while it is on. */
  newCompany: boolean;
  /** The release switch `platform-support-session`: Open company shows only while it is on. */
  support: boolean;
  /** The release switch `platform-company-records`: Details and Logs show only while it is on. */
  records: boolean;
  /** The request came to the super instance's host (HQ_HOST). */
  hq: boolean;
}

/** One entry of the super instance's Company selector. */
export interface HqCompanyDto {
  id: string;
  name: string;
  stage: CompanyStage;
  status: 'Active' | 'Inactive';
  /** 212Ez's own company, to test and rehearse. */
  sandbox: boolean;
}

export interface SignupResultDto {
  companyName: string;
  loginId: string;
  stage: CompanyStage;
}

/** One company as the platform console lists it. */
export interface CompanyDto {
  id: string;
  name: string;
  status: 'Active' | 'Inactive';
  stage: CompanyStage;
  createdAt: string;
  /** Who signed it up, when it signed itself up. */
  contact: { name: string; email: string; phone: string; carriers: string } | null;
  adminLoginIds: string[];
  /** Its Active administrators, for "Temporary password". */
  admins: Array<{ id: string; loginId: string; locked: boolean }>;
  /** Set when the company asked to go Live (Get started > Request Live). */
  liveRequest: LiveRequestDto | null;
  /** Carrier connector requests not yet Done or Declined. */
  openRequests: number;
  users: number;
  customers: number;
  carriersConnected: number;
  connectors: number;
}

export const companyUpdateSchema = z
  .object({
    stage: z.enum(COMPANY_STAGES).optional(),
    status: z.enum(['Active', 'Inactive']).optional(),
  })
  .refine((v) => v.stage || v.status, 'Nothing to change');

/** A carrier connector made by a company, as the platform console lists them. */
export interface PlatformConnectorDto {
  id: string;
  label: string;
  companyId: string;
  companyName: string;
  shared: boolean;
  actions: number;
  usedBy: number;
  updatedAt: string;
}

export const connectorShareSchema = z.object({ shared: z.boolean() });

/* ------------------------------ Get started ------------------------------ */

/** The steps of Settings > Get started, in order; each checklist item belongs to one. */
export const ONBOARDING_STEPS = [
  { key: 'carriers', title: 'Connect your carriers' },
  { key: 'selling', title: 'What and where you sell' },
  { key: 'services', title: 'Payments, messages and tax' },
  { key: 'people', title: 'Your staff and agents' },
  { key: 'customers', title: 'Customers you already have' },
  { key: 'platform', title: 'Done by the platform' },
  { key: 'live', title: 'Go live' },
] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number]['key'];

export interface LiveRequestDto {
  at: string;
  by: string;
  note: string;
}

export const liveRequestSchema = z.object({ note: z.string().trim().max(500).default('') });
export type LiveRequestInput = z.input<typeof liveRequestSchema>;

export const CONNECTOR_REQUEST_STATUSES = ['Open', 'In progress', 'Done', 'Declined'] as const;
export type ConnectorRequestStatus = (typeof CONNECTOR_REQUEST_STATUSES)[number];

/** "My carrier is not listed": a company asks the platform for a connector to its carrier. */
export const connectorRequestSchema = z.object({
  carrierName: z.string().trim().min(2, 'Carrier or MVNE is required').max(80),
  docsUrl: z
    .string()
    .trim()
    .max(300)
    .refine((v) => !v || /^https:\/\/\S+$/i.test(v), 'Enter an https:// address or leave it empty')
    .default(''),
  contactName: z.string().trim().max(80).default(''),
  contactEmail: z
    .string()
    .trim()
    .max(80)
    .refine((v) => !v || z.string().email().safeParse(v).success, 'Enter a valid email address')
    .default(''),
  notes: z.string().trim().max(2000).default(''),
});
export type ConnectorRequestInput = z.input<typeof connectorRequestSchema>;

export interface ConnectorRequestDto {
  id: string;
  carrierName: string;
  docsUrl: string;
  contactName: string;
  contactEmail: string;
  notes: string;
  status: ConnectorRequestStatus;
  /** The platform's answer (e.g. which shared connector to pick). */
  reply: string;
  companyName: string;
  requestedBy: string;
  createdAt: string;
  updatedAt: string;
}

export const connectorRequestUpdateSchema = z.object({
  status: z.enum(CONNECTOR_REQUEST_STATUSES),
  reply: z.string().trim().max(1000).default(''),
});

export interface OnboardingDto {
  company: { name: string; stage: CompanyStage; status: 'Active' | 'Inactive' };
  liveRequest: LiveRequestDto | null;
  items: ReadinessItemDto[];
  requests: ConnectorRequestDto[];
  /** The sample set-up is in this company and can be removed. */
  sample: boolean;
}

/** The logs the platform console reads, one kind at a time. */
export const PLATFORM_LOG_KINDS = [
  'api',
  'audit',
  'messages',
  'payments',
  'refunds',
  'address',
  'callbacks',
] as const;
export const PLATFORM_LOG_LABELS: Record<(typeof PLATFORM_LOG_KINDS)[number], string> = {
  api: 'Carrier and API calls (carriers, NLAD, National Verifier, payments, tax, shipping)',
  audit: 'Audit trail (who changed what)',
  messages: 'Messages sent (e-mail, SMS, IVR)',
  payments: 'Payments and invoices',
  refunds: 'Refunds',
  address: 'Address checks (USPS)',
  callbacks: 'Calls from carriers (port and line events)',
};

/** Platform console > Logs: every company's transactions and connections, one kind at a time. */
export const platformLogQuerySchema = z.object({
  kind: z.enum(PLATFORM_LOG_KINDS).default('api'),
  companyId: z
    .string()
    .regex(/^[a-f\d]{24}$/i, 'Invalid id')
    .optional()
    .or(z.literal('').transform(() => undefined)),
  status: z
    .enum(['success', 'failure'])
    .optional()
    .or(z.literal('').transform(() => undefined)),
  q: z.string().trim().max(100).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type PlatformLogQuery = z.input<typeof platformLogQuerySchema>;

export interface PlatformLogDto {
  id: string;
  at: string;
  company: string;
  /** API: provider and action. Audit: action, record and its id. */
  what: string;
  who: string;
  /** API: success or failure. */
  result: string;
  /** The request and answer, or the change, as JSON (cut at 2000 characters). */
  detail: string;
}

export interface PlatformLogPage {
  data: PlatformLogDto[];
  page: number;
  pageSize: number;
  total: number;
}

interface LinkStateDto {
  name: string;
  provider: string;
  mode: string;
  status: string;
  tested: { ok: boolean; at: string } | null;
}

/** Platform console > Details: a company's connections (never their secrets) and its records. */
export interface CompanyOverviewDto {
  id: string;
  name: string;
  stage: CompanyStage;
  status: 'Active' | 'Inactive';
  services: LinkStateDto[];
  carriers: LinkStateDto[];
  counts: {
    customers: Record<string, number>;
    enrollments: number;
    plans: number;
    freeSims: number;
    zips: number;
    users: number;
  };
}

/** HQ analytics (owner, 2026-11-05): every company summed, or one company. */
export const hqAnalyticsQuerySchema = z.object({
  companyId: z
    .string()
    .regex(/^[a-f\d]{24}$/i, 'Invalid id')
    .optional()
    .or(z.literal('').transform(() => undefined)),
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a date')
    .optional()
    .or(z.literal('').transform(() => undefined)),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a date')
    .optional()
    .or(z.literal('').transform(() => undefined)),
});
export type HqAnalyticsQuery = z.input<typeof hqAnalyticsQuerySchema>;

export interface HqFigures {
  /** Lines that are not disconnected (Active, Suspended, Dispatched). */
  lines: number;
  prepaid: number;
  postpaid: number;
  lifeline: number;
  charity: number;
  activations: number;
  disconnects: number;
  portIns: number;
  portOuts: number;
  /** Money taken in the period (wallet-funded payments left out: that money was counted when it came in). */
  revenueCents: number;
  taxCents: number;
  refundsCents: number;
  /** Revenue minus tax and refunds. */
  netRevenueCents: number;
  /** Lines times each carrier's wholesale cost per line per month, for the days of the period. */
  wholesaleCostCents: number;
  /** Net revenue minus wholesale cost: an estimate. */
  profitCents: number;
}

export interface HqCompanyFigures extends HqFigures {
  id: string;
  name: string;
  stage: CompanyStage;
  status: 'Active' | 'Inactive';
}

export interface HqCarrierFigures {
  id: string;
  companyId: string;
  company: string;
  code: string;
  name: string;
  lines: number;
  /** Share of all lines in the view. */
  sharePercent: number;
  costPerLineCents: number;
  monthlyCostCents: number;
}

export interface HqAnalyticsDto {
  from: string;
  to: string;
  totals: HqFigures;
  companies: HqCompanyFigures[];
  carriers: HqCarrierFigures[];
}

export const carrierCostSchema = z.object({
  costPerLineCents: z.coerce.number().int().min(0).max(100_000),
});
